import { QrCode, Download, Printer, Palette, Gift } from "lucide-react";
import { API } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const SITE_URL = "https://artbyjasoncaricatures.com";
const REVIEW_URL = `${SITE_URL}/testimonials`;
const QR_SRC = `${API}/review-qr`;

const PALETTE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r=".5" fill="#fff"/><circle cx="17.5" cy="10.5" r=".5" fill="#fff"/><circle cx="8.5" cy="7.5" r=".5" fill="#fff"/><circle cx="6.5" cy="12.5" r=".5" fill="#fff"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>`;

export default function ReviewQRTab() {
  const { settings } = useSettings();
  const shopName = settings?.shop_name || "Art by Jason Caricatures";

  const printSign = () => {
    const w = window.open("", "_blank", "width=850,height=1100");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${shopName} — Review Table Sign</title>
      <style>
        @page { margin: 0; size: portrait; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { margin: 0; font-family: 'Trebuchet MS','Segoe UI',sans-serif; color: #0F172A; background: #FFFDF7;
               display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 40px; }
        .sign { width: 680px; text-align: center; background: #FFFDF7; border: 9px solid #0F172A; border-radius: 36px;
                padding: 44px 46px 48px; box-shadow: 14px 14px 0 0 #0F172A; }
        .brand { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 26px; }
        .brand .mark { width: 56px; height: 56px; border-radius: 16px; background: #FF5C9E; border: 4px solid #0F172A;
                       display: grid; place-items: center; box-shadow: 5px 5px 0 0 #0F172A; }
        .brand .name { font-size: 26px; font-weight: 800; letter-spacing: -.3px; }
        h1 { font-size: 58px; line-height: 1.02; margin: 6px 0 4px; font-weight: 800; }
        h1 span { color: #FF5C9E; }
        .sub { font-size: 24px; color: #475569; margin: 0 0 26px; font-weight: 600; }
        .qrwrap { display: inline-block; background: #fff; border: 6px solid #0F172A; border-radius: 26px;
                  padding: 18px; box-shadow: 7px 7px 0 0 #0F172A; }
        .qrwrap img { display: block; width: 340px; height: 340px; }
        .steps { font-size: 17px; color: #475569; margin-top: 18px; font-weight: 600; }
        .hook { margin-top: 26px; background: #FFDE59; border: 5px solid #0F172A; border-radius: 22px;
                padding: 18px 22px; box-shadow: 7px 7px 0 0 #0F172A; }
        .hook .big { font-size: 34px; font-weight: 800; line-height: 1; }
        .hook .big b { color: #FF5C9E; }
        .hook .small { font-size: 16px; font-weight: 700; margin-top: 8px; color: #0F172A; }
        .url { margin-top: 22px; font-size: 18px; font-weight: 800; }
      </style></head>
      <body onload="setTimeout(function(){window.print();},450)">
        <div class="sign">
          <div class="brand"><span class="mark">${PALETTE_SVG}</span><span class="name">${shopName}</span></div>
          <h1>Loved your <span>caricature?</span></h1>
          <p class="sub">Scan the code &amp; leave a quick review!</p>
          <div class="qrwrap"><img src="${QR_SRC}" alt="Scan to review" /></div>
          <p class="steps">Open your camera &middot; point at the code &middot; tap the link</p>
          <div class="hook">
            <div class="big"><b>10% OFF</b> your next order</div>
            <div class="small">Leave a review &amp; mention it when you order again 💛</div>
          </div>
          <div class="url">${REVIEW_URL.replace("https://", "")}</div>
        </div>
      </body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="admin-review-qr" className="max-w-3xl">
      <h1 className="font-display font-bold text-3xl mb-1 flex items-center gap-2">
        <QrCode className="w-7 h-7" /> Review QR &amp; Event Sign
      </h1>
      <p className="text-ink/60 mb-6">
        Print the table sign and set it out at events or hand it over at pickup. Customers scan it with their
        phone camera, land on your review page, and get a 10%-off nudge for their next order.
      </p>

      {/* On-screen preview mirrors the printed sign */}
      <div className="brut-card p-6 sm:p-8 bg-cream-solid text-center" data-testid="sign-preview">
        <div className="flex items-center justify-center gap-2 mb-5">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-bubble border-[3px] border-ink shadow-brut">
            <Palette className="w-5 h-5 text-white" strokeWidth={2.5} />
          </span>
          <span className="font-display font-bold text-lg">{shopName}</span>
        </div>

        <h2 className="font-display font-bold text-3xl sm:text-4xl">Loved your <span className="text-bubble">caricature?</span></h2>
        <p className="text-ink/60 text-lg mt-1 mb-5">Scan the code &amp; leave a quick review!</p>

        <div className="inline-block bg-white p-4 border-[3px] border-ink rounded-2xl shadow-brut">
          <img src={QR_SRC} alt="Review page QR code" className="w-56 h-56 block" data-testid="review-qr-img" />
        </div>

        <div className="mt-6 max-w-sm mx-auto brut border-ink bg-sun p-4 shadow-brut" data-testid="offer-hook">
          <div className="flex items-center justify-center gap-2 font-display font-bold text-2xl">
            <Gift className="w-6 h-6" /> <span><span className="text-bubble">10% OFF</span> your next order</span>
          </div>
          <p className="text-sm font-bold mt-1">Leave a review &amp; mention it when you order again 💛</p>
        </div>

        <p className="font-display font-bold mt-5 break-all" data-testid="review-qr-url">{REVIEW_URL}</p>

        <div className="flex flex-wrap gap-3 justify-center mt-6">
          <button onClick={printSign} className="brut-btn bg-bubble text-white" data-testid="print-poster-btn">
            <Printer className="w-5 h-5" /> Print table sign
          </button>
          <a href={`${QR_SRC}?download=true`} download="art-by-jason-review-qr.png"
            className="brut-btn bg-white" data-testid="download-qr-btn">
            <Download className="w-5 h-5" /> Download QR image
          </a>
        </div>
      </div>

      <div className="brut-card p-5 mt-5 bg-sky/30 text-sm text-ink/70">
        <b>Tip:</b> The code always points to <span className="font-bold">{REVIEW_URL}</span>. Test it once with your
        own phone before printing a big batch.
      </div>
    </div>
  );
}
